import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { LocalStorage } from 'src/utils/storage/local-storage';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ValidationTool from 'src/utils/validationHelper';
import landingImage from '../../assets/app-image/landing.png';
import useResponsive from '../../hooks/useResponsive';

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledSection = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 480,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default,
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
}));

const AppSumoRegisteration = () => {
  const { t: translate } = useTranslation();
  const mdUp = useResponsive('up', 'md');
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [formSchema, setFormSchema] = useState({});
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const { showToast } = useToaster();
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [timeZone, setTimeZone] = useState('UTC');
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const appLogo = CommonHelper.GetAppLogo();

  const generateFormModelSchema = useCallback(() => {
    const fields = [
      ValidationTool.GenerateSchema('name', translate('profile.general.first_name'), 'TEXT', true),
      ValidationTool.GenerateSchema('email', translate('profile.general.email'), 'EMAIL', true),
      ValidationTool.GenerateSchema('password', translate('profile.general.password'), 'TEXT', true),
      ValidationTool.GenerateSchema('referral_code', translate('common.coupon_code'), 'TEXT', true),
    ];
    const schema = ValidationTool.GenerateSchemaModel(fields, translate);
    setFormSchema(schema);
  }, [translate]);

  useEffect(() => {
    const localTimeZone = CommonHelper.GetLocalTimeZone();
    if (localTimeZone) {
      setTimeZone(localTimeZone);
    }
    const token = LocalStorage.getItem('token');
    if (token) navigate('/new-post');
    else generateFormModelSchema();
  }, [generateFormModelSchema, navigate]);

  const handleInputChange = (event) => {
    const value = event.target.value;
    const name = event.target.name;
    switch (name) {
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'name':
        setName(value);
        break;
      case 'referral_code':
        setReferralCode(value);
        break;
      default:
        break;
    }
  };

  const handleOnBlur = (event) => {
    const fieldName = event.target.name;
    const fieldValue = event.target.value;
    const validationErrors = { ...formValidationErrors };
    const message = ValidationTool.ValidateInput(formSchema, fieldValue, fieldName);
    if (message) validationErrors[fieldName] = message;
    else validationErrors[fieldName] = '';
    setFormValidationErrors(validationErrors);
  };

  const handleRegister = () => {
    const registerForm = {
      name,
      email,
      password,
      referral_code: referralCode,
      device_type: isMobile ? CommonHelper.DeviceType.Mobile : CommonHelper.DeviceType.Desktop,
    };
    const validation = ValidationTool.FormValidation(formSchema, registerForm);
    if (validation.isValidForm) handleCreateNewUser();
    else setFormValidationErrors(validation.formValidationErrors);
  };
  const handleCreateNewUser = () => {
    setLoading(true);
    const userInfo = {
      first_name: name,
      email: email,
      password: password,
      timezone: timeZone,
    };
    const registerForm = {
      user_info: userInfo,
      appsumo_token: referralCode,
    };
    const postProps = {
      url: userModuleURL.appSumoRegisteration,
      body: registerForm,
      successCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };
  const successCallback = (data, message) => {
    message && showToast(message);
    data['is_register'] = true;
    data['is_coupon_register'] = true;
    CommonHelper.AppLoginCall(data);
  };
  const failureCallback = (message) => {
    message && showToast(message, 'e');
    if (
      message &&
      (message.toLowerCase().includes('redeem code not found') ||
       message.toLowerCase().includes('not found') ||
       message.toLowerCase().includes('invalid code') ||
       message.toLowerCase().includes('coupon not found'))
    ) {
      setErrorMessage(
        "The code you entered was not found.\n\n" +
        "Please double-check the code.\n" +
        "Example format: Dealfuel-PROFESSIONAL-xxxx-xxxx-xxxx-xxxx"
      );
      setErrorDialogOpen(true);
    }
    setLoading(false);
  };
  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
  };
  return (
    <>
      <Helmet>
        <title>{translate('common.register')}</title>
      </Helmet>

      <StyledRoot>
        {mdUp && (
          <>
            <img
              src={appLogo}
              alt={`${CommonHelper.GetAppName()} logo`}
              style={{
                height: '40px',
                position: 'fixed',
                top: '34px',
                left: '35px',
              }}
            />
            <StyledSection>
              <Typography variant="h3" sx={{ px: 5, mt: 10, mb: 5 }}>
                {translate('registration.welcome_to')} {CommonHelper.GetAppName()}
              </Typography>
              <img src={landingImage} alt="Register" />
            </StyledSection>
          </>
        )}
        <Container maxWidth="sm">
          <StyledContent>
            <Typography variant="h4" sx={{ mb: 3 }} gutterBottom>
              {translate('registration.register_in')} {CommonHelper.GetAppName()}
            </Typography>

            <Stack spacing={3} sx={{ mb: 5 }}>
              <TextField
                error={formValidationErrors?.name}
                color={name === '' ? 'error' : 'success'}
                name="name"
                label={translate('common.name')}
                onChange={handleInputChange}
                value={name}
                helperText={formValidationErrors?.name}
                onBlur={handleOnBlur}
              />
              <TextField
                error={formValidationErrors?.email}
                color={email === '' ? 'error' : 'success'}
                name="email"
                label={translate('profile.general.email')}
                onChange={handleInputChange}
                value={email}
                helperText={formValidationErrors?.email}
                onBlur={handleOnBlur}
              />
              <TextField
                error={formValidationErrors?.password}
                color={password === '' ? 'error' : 'success'}
                name="password"
                label={translate('profile.general.password')}
                type={showPassword ? 'text' : 'password'}
                onChange={handleInputChange}
                onBlur={handleOnBlur}
                value={password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={formValidationErrors?.password}
              />
              <TextField
                required
                error={Boolean(formValidationErrors?.referral_code)}
                helperText={formValidationErrors?.referral_code}
                name="referral_code"
                placeholder="Dealfuel-PROFESSIONAL-xxxx-xxxx-xxxx-xxxx"
                label={translate('common.coupon_code')}
                onChange={handleInputChange}
                onBlur={handleOnBlur}
                value={referralCode}
              />
            </Stack>
            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              loading={loading}
              variant="contained"
              onClick={handleRegister}
            >
              {translate('common.register')}
            </LoadingButton>
          </StyledContent>
        </Container>
      </StyledRoot>
      <Dialog
        open={errorDialogOpen}
        onClose={handleCloseErrorDialog}
        aria-labelledby="error-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="error-dialog-title" sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          Code Not Found
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
            {errorMessage}
          </DialogContentText>
          <Alert severity="error" sx={{ mt: 2 }}>
            Please check your code and try again.
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseErrorDialog} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default AppSumoRegisteration;
